import { TranslocoService } from '@jsverse/transloco';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonMap } from '../../../_shared';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CommonMapService } from '../../../../_shared/services/map/common-map.service';
import { FuelChangesLocations, FuelChangesRequest } from '../../models/fuel-changes-details.model';
import { IClusterRenderer } from '../../../../_shared/interfaces/maps/base-cluster-renderer';
import { IMarkerFactory } from '../../../../_shared/interfaces/maps/base-marker-factory';
import { FuelChangesClusterRenderer } from '../../helpers/maps/fuel-changes-cluster-renderer.helper';
import { FuelChangesMarkerFactory } from '../../helpers/maps/fuel-changes-marker-factory.helper';
import { IMapInfoWindowBuilder } from '../../../../_shared/interfaces/maps/base-map-info-window-builder';
import { FuelChangesMapInfoWindowBuilder } from '../../helpers/maps/fuel-changes-map-info-window-builder.helper';
import { Subject, Subscription } from 'rxjs';
import { AuthService } from '../../../../_shared/services';
import { CurrencyHelper } from '../../../../_shared/helpers/currency/currency-map.helper';
import { AccountFilterData, DateIntervalFilterData } from '../../../../_shared/models';
import { FuelChangesService } from '../../services/fuel-changes.service';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { FuelChangeTypeFilter } from '../../enums/fuel-changes-type-filter.enum';
import { PageFilterStateService } from '../../../../_shared/services/page-filter-state.service';

@Component({
  selector: 'app-fuel-changes-map',
  standalone: true,
  imports: [CommonMap, RouterModule],
  templateUrl: './fuel-changes-map.component.html',
  styleUrl: './fuel-changes-map.component.scss'
})
export class FuelChangesMapComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  private mapDataService = inject(CommonMapService);
  private activatedRoute = inject(ActivatedRoute);
  private auth = inject(AuthService);
  private pageFilterStateService = inject(PageFilterStateService);
  private fuelChangesService = inject(FuelChangesService);
  private errorService = inject(ErrorHandlerService);

  @Input() mapCenter: google.maps.LatLngLiteral | null = null;
  @Input() isLoading: boolean = false;

  dataSubscription: Subscription;
  fuelChangesData: FuelChangesLocations[] = [];
  selectedItem: string = '';
  routeLink: string = '';
  isCollapsed: boolean = false;
  clusterRenderer: IClusterRenderer = new FuelChangesClusterRenderer();
  markerFactory: IMarkerFactory = new FuelChangesMarkerFactory();
  mapInfoWindowBuilder: IMapInfoWindowBuilder = new FuelChangesMapInfoWindowBuilder(key => this.transloco.translate(key));
  mapTypeControl: boolean = true;
  mapTypeId: string = 'roadmap';
  currencyCode: string = '';
  currencySymbol: string = '';
  dateFilter = {} as DateIntervalFilterData;
  accountFilter = {} as AccountFilterData;
  searchTerm: string = '';
  fuelChangeType: FuelChangeTypeFilter = FuelChangeTypeFilter.All;
  fuelChangesLocations: any[] = [];
  private cancelSubject = new Subject<void>();

  ngOnInit(): void {
    this.currencyCode = this.auth.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);

    // Restore map type from localStorage
    const savedMapType = localStorage.getItem('webtrack-map-type');
    if (savedMapType && (savedMapType === 'roadmap' || savedMapType === 'satellite' || savedMapType === 'hybrid')) {
      this.mapTypeId = savedMapType;
    }

    this.setRouteLink();
    this.loadPageFilterState();

    this.dataSubscription = this.mapDataService.data$.subscribe((data) => {
      if (!this.isCollapsed && data.length === 0) {
        this.loadFuelChangesLocations();
      }

      this.fuelChangesData = data;
    });
  }

  private async loadFuelChangesLocations(): Promise<void> {
    try {
      if (!this.accountFilter.accountIds || !this.dateFilter.startDate || !this.dateFilter.endDate) {
        return;
      }

      const request: FuelChangesRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate,
        timeTo: this.dateFilter.endDate,
        searchTerm: this.searchTerm,
        fuelChangeType: this.fuelChangeType
      };

      this.cancelSubject.next();
      const response = await this.fuelChangesService.getFuelChangesLocations(request, this.cancelSubject);

      if (response.data) {
        this.fuelChangesLocations = response.data?.map(item => {
          return {
            assetId: item.assetId,
            vehicle: item.assetName,
            driver: `${item.firstName} ${item.lastName}`,
            amount: item.amount,
            cost: item.cost,
            type: item.fuelStatusType,
            lat: item.lat,
            lng: item.lng,
            location: item.location,
            timestamp: item.timestamp,
            tank: item.tank,
            fuelConsumption: item.fuelConsumption,
            currentFuelLevel: item.currentFuelLevel,
            fuelLevelBeforeChange: item.fuelLevelBeforeChange,
            fuelLevelAfterChange: item.fuelLevelAfterChange,
            distanceUntilEmpty: item.distanceUntilEmpty,
            isValidFuelLevel: item.isValidFuelLevel
          };
        }) ?? [];
        this.mapDataService.updateData(this.fuelChangesLocations);
      }
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private loadPageFilterState(): void {
    const pageFilter = this.pageFilterStateService.getFilter<any>();
    if (pageFilter) {
      this.dateFilter = pageFilter as DateIntervalFilterData;
      this.accountFilter = pageFilter as AccountFilterData;
      this.fuelChangeType = pageFilter.fuelChangeType;
      this.searchTerm = pageFilter.searchTerm;
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
    if (!this.isCollapsed) {
      this.mapDataService.updateData([]);
      this.dataSubscription?.unsubscribe();
    }
  }

  setRouteLink(): void {
    const currentPage = this.activatedRoute.snapshot.routeConfig?.path || '';
    this.isCollapsed = !currentPage.includes('map');
    this.routeLink = this.isCollapsed ? '/fuel-management/fuel-changes/fuel-changes-map' : '/fuel-management/fuel-changes';

    // Read mapTypeId from query params if available
    const mapType = this.activatedRoute.snapshot.queryParams['mapType'];
    if (mapType && (mapType === 'roadmap' || mapType === 'satellite' || mapType === 'hybrid')) {
      this.mapTypeId = mapType;
    }
  }
}